import React, { useState, useCallback, useEffect } from 'react';
import { useToggle } from 'react-use-v13';
import Textarea from 'design-system/Textarea';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import SelectProject from 'corp/components/UI/SelectProject';
import { Label, Button } from 'design-system/Form';
import { Checkbox } from 'design-system';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import Select from 'corp/components/UI/Select';
import { IUserJson } from '@scale/shared/types/user';
import { invokeDynamicCorpJob } from '@scale/frontend-lib/invokeCorpJob';
import { v4 as uuidv4 } from 'uuid';

import {
  IDynamicCorpJobResponse,
  formatDynamicCorpJobResponse,
} from 'corp/views/RoadrunnerUtilsPage';

const description = `
### Description:
- This tool allows you to create a delivery batch and spreadsheet for Roadrunner projects
- Select the project, list the TASKS to deliver, any edge case TASKS, and then click the button to create
- Canceled tasks will be considered as sensitive content

NOTE: results of any run can be previewed on google sheets here: https://docs.google.com/spreadsheets/d/1NNli0Of9Uw5ex2sd_-XDvOrfq4C-itvRAmekHBIz5mk/edit#gid=1425647214

##### Author: Ahmad Elshenawy, Kamesh Vedula
---
`;

const generalTaxonomyTextDescription = `#### General Taxonomy projects can have metadata set on the project pointing to a gsheet with the specific Roadrunner taxonomy

- Example taxonomy file: 
  - [link](https://docs.google.com/spreadsheets/d/1ci95xdtj_vobxxw7WKtdoZLSnvnQw6ifB2dw9XPCuS0/edit#gid=1203611767)
- Example Project metadata (ask Eng/FE to set if needed)
  - \`metadata.custom.taxonomySheetId = 'GOOGLE_SHEET_ID'\`
`;

interface SelectOption {
  label: string;
  value: any;
}

enum QueryState {
  Ready,
  Executing,
  Pending,
}

const projectIdentifiers: string[] = ['General', 'SFV', 'Comedy', 'Genre_Predictions'];

const GenerateDelivery: React.FC<{ user: IUserJson }> = ({ user }) => {
  const [deliveryIds, setDeliveryIds] = useState<string>();
  const [edgeCaseIds, setEdgeCaseIds] = useState<string | null | undefined>();

  const [projectIdentifier, setProjectIdentifier] = useState<SelectOption | null>();
  const [dryRun, toggleDryRun] = useToggle(true);
  const [relaxQuery, toggleRelaxQuery] = useToggle(false);
  const [allowRedelivery, toggleAllowRedelivery] = useToggle(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();

  const [runUuid, setUuid] = useState<string>();

  const [infoMessage, setInfoMessage] = useState<string>();

  const [project, setProject] = useState<SelectOption | null>();
  const enabled = deliveryIds && projectIdentifier && !loading;

  const [queryState, setQueryState] = useState<QueryState>(QueryState.Ready);

  const handleProjectIdentifier = async (projectIdentifier: SelectOption) => {
    setProjectIdentifier(projectIdentifier);
  };

  const handleUpdate = async () => {
    setLoading(true);
    setMessage('');
    setInfoMessage('');
    setQueryState(QueryState.Executing);

    const newRunUUID = uuidv4();
    setUuid(newRunUUID);

    const generateDeliveryArgs = {
      inputProjectId: project?.value,
      projectDescription: projectIdentifier!.value,
      taskIdsString: deliveryIds,
      edgeCaseTaskIdsString: edgeCaseIds,
      dryRun,
      allowRedelivery,
      user,
      relaxQuery,
      runUuid: newRunUUID,
    };

    try {
      const job = await invokeDynamicCorpJob(
        'flamingo-generate-delivery',
        generateDeliveryArgs,
        true,
      );
      let currentResponse: IDynamicCorpJobResponse;
      if (job.status === 'errored') {
        currentResponse = {
          message: job.errorReason || 'Internal server error',
          success: false,
        };
      } else {
        const results = job.jsonOutput;
        currentResponse = {
          message: results?.message || 'Successful!',
          success: true,
          corpJobOrSfnId: results?.stepFunctionId || job.id,
        };
      }

      setLoading(false);
      setMessage(formatDynamicCorpJobResponse(currentResponse));
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setLoading(false);
    } finally {
      setQueryState(QueryState.Pending);
      setInfoMessage('Job Running: Run results will post here when finished...');
    }
  };

  const fetchData = useCallback(async () => {
    if (queryState !== QueryState.Pending) return;
    const { success, error, message } = await apiFetch(
      null,
      `corp-api/roadrunner-utils/outputs/${runUuid}`,
    );
    if (success) {
      setInfoMessage(message);
      setQueryState(QueryState.Ready);
    } else if (!success && error) {
      setInfoMessage(error);
    }
  }, [runUuid, queryState]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchData, runUuid, queryState]);

  const handleProject = async (project: SelectOption) => {
    setProject(project);
  };

  const buttonText = () => {
    if (!deliveryIds) return 'Specify task IDs to deliver';
    if (!project) return 'Select project';
    if (!projectIdentifier) return 'Select project type';
    return 'Create delivery';
  };

  const renderOutput = (text: string) => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={text || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>

      <section className="two-columns">
        <section>
          <Label>Task IDs:</Label>
          <Textarea
            rows={5}
            placeholder={'Task IDs'}
            style={{ paddingRight: '16px' }}
            value={deliveryIds}
            onChange={e => setDeliveryIds(e.target.value)}
          />
        </section>
        <section className={deliveryIds ? '' : 'disabled'} style={{ marginLeft: '16px' }}>
          <Label>Edge Case Task IDs (optional):</Label>
          <Textarea
            rows={5}
            placeholder={'Edge Case Task IDs'}
            // @ts-ignore TODO: FIXME [TS2322]
            value={edgeCaseIds}
            style={{ paddingRight: '16px' }}
            onChange={e => setEdgeCaseIds(e.target.value)}
          />
        </section>
      </section>
      <br />
      <br />
      <hr />
      <section className="two-columns">
        <section>
          <Label>Project:</Label>
          <SelectProject value={project} onChange={handleProject} />
        </section>
        <section className={enabled ? '' : 'disabled'}>
          <Label>Project Type:</Label>
          <Select
            className={deliveryIds ? '' : 'disabled'}
            value={projectIdentifier}
            onChange={handleProjectIdentifier}
            disabled={!project}
            style={{ width: '50%' }}
            options={projectIdentifiers.map((i: string) => {
              return { label: i, value: i };
            })}
          />
          {projectIdentifier && projectIdentifier.value === 'General' && (
            <section>
              <SanitizedMarkdown text={generalTaxonomyTextDescription} />
            </section>
          )}
        </section>
      </section>
      <section className="two-columns">
        <section className={enabled ? '' : 'disabled'}>
          <Label>Allow redelivery of task(s)?:</Label>
          <Checkbox
            checked={allowRedelivery}
            label="Allow Redelivery of already delivered tasks?"
            onChange={_value => {
              toggleAllowRedelivery(!allowRedelivery);
            }}
          />
          <Label>Bypass strict task status checks?:</Label>
          <Checkbox
            checked={relaxQuery}
            label="Relax task restrictions (ignore non-valid-status and last-attempt-reviewLevel-filtering)"
            onChange={_value => {
              toggleRelaxQuery(!relaxQuery);
            }}
          />
        </section>
      </section>
      <br />
      <section>
        <h3>Dry Run</h3>
        <Checkbox
          checked={dryRun}
          label="Dry Run"
          onChange={_value => {
            toggleDryRun(!dryRun);
          }}
        />
        <hr />
        <Button primary disabled={!enabled || loading} onClick={handleUpdate}>
          {buttonText()}
        </Button>
        {message && renderOutput(message)}
        {infoMessage && <hr />}
        {infoMessage && renderOutput(infoMessage)}
      </section>
      <style jsx>{`
        .root .two-columns {
          display: flex;
        }
        .root .two-columns section {
          flex-basis: 50%;
        }
        .root .two-columns section.disabled {
          color: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default GenerateDelivery;
